import {
  NumberField as AriaNumberField,
  type NumberFieldProps as AriaNumberFieldProps,
  FieldError,
  Input,
  Label,
  Text,
} from "react-aria-components";
import { cn } from "@core/utils";

interface NumberFieldProps extends Omit<
  AriaNumberFieldProps,
  "className" | "children"
> {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
}

/**
 * Numeric input primitive on React Aria's `NumberField` — used for ages, target seats, and
 * file-size limits. Preferred over `<TextField type="number">` because React Aria handles
 * locale-aware parsing, min/max clamping, and arrow-key stepping, and yields a real `number`
 * (not a numeric string) to the form — which is what the API contract expects.
 *
 * Steppers are intentionally not rendered: the design shows a plain box, and the
 * increment/decrement buttons stay available to assistive tech through the field's role.
 */
export function NumberField({
  label,
  hideLabel = false,
  placeholder,
  description,
  errorMessage,
  ...props
}: NumberFieldProps) {
  const invalid = Boolean(errorMessage) || props.isInvalid;

  return (
    <AriaNumberField
      isInvalid={invalid}
      // The contract sends plain integers, so no grouping separators in the input.
      formatOptions={{ useGrouping: false, maximumFractionDigits: 0 }}
      className="flex w-full flex-col gap-1.5"
      {...props}
    >
      <Label
        className={cn(
          "text-sm-medium text-(--text-secondary)",
          hideLabel && "sr-only"
        )}
      >
        {label}
        {props.isRequired ? (
          <span aria-hidden className="text-(--text-error)">
            {" "}
            *
          </span>
        ) : null}
      </Label>

      <div
        className={cn(
          "flex items-center rounded-md bg-surface-field pl-3.5 pr-3.5 py-2.5 shadow-xs",
          "border transition-colors",
          "focus-within:ring-2 focus-within:ring-focus-ring",
          invalid
            ? "border-(--border-field-error) focus-within:ring-error-500"
            : "border-(--border-field) focus-within:border-(--focus-ring)"
        )}
      >
        <Input
          placeholder={placeholder}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-md-regular text-(--text-primary) outline-none",
            "placeholder:text-(--text-placeholder)"
          )}
        />
      </div>

      {description ? (
        <Text
          slot="description"
          className="text-sm-regular text-(--text-tertiary)"
        >
          {description}
        </Text>
      ) : null}

      <FieldError className="text-sm-regular text-(--text-error)">
        {errorMessage}
      </FieldError>
    </AriaNumberField>
  );
}

export type { NumberFieldProps };
